import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Link,
  CircularProgress,
} from '@mui/material';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import { useSnackbar } from 'notistack';
import { login, forgotPassword, resetPasswordWithOtp } from '../../store/slices/authSlice';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading } = useSelector((s) => s.auth);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (forgotMode) {
      const result = await dispatch(forgotPassword(email));
      if (forgotPassword.fulfilled.match(result)) {
        enqueueSnackbar(result.payload || "Si l'e-mail existe, un code vous a été envoyé.", { variant: 'success' });
        setResetMode(true);
        setResetEmail(email);
        setForgotMode(false);
      } else {
        enqueueSnackbar(result.payload || 'Erreur.', { variant: 'error' });
      }
      return;
    }
    if (resetMode) {
      const result = await dispatch(resetPasswordWithOtp({ email: resetEmail, otp, newPassword }));
      if (resetPasswordWithOtp.fulfilled.match(result)) {
        enqueueSnackbar('Mot de passe réinitialisé. Connectez-vous.', { variant: 'success' });
        setResetMode(false);
        setOtp('');
        setNewPassword('');
      } else {
        enqueueSnackbar(result.payload || 'Code invalide ou expiré.', { variant: 'error' });
      }
      return;
    }
    const result = await dispatch(login({ email, password }));
    if (login.fulfilled.match(result)) {
      enqueueSnackbar('Connexion réussie.', { variant: 'success' });
      if (result.payload.mustChangePassword) {
        navigate('/dashboard/change-password');
      } else {
        navigate('/dashboard');
      }
    } else {
      enqueueSnackbar(result.payload || 'E-mail ou mot de passe incorrect.', { variant: 'error' });
    }
  };

  return (
    <Box
      className="min-h-screen flex items-center justify-center notranslate"
      translate="no"
      sx={{
        position: 'relative',
        p: 2,
        backgroundImage: 'url(/ung-login.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: 'rgba(255,255,255,0.01)',
          backdropFilter: 'blur(4px)',
          zIndex: 0,
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 420 }}
      >
        <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h5" fontWeight="bold" color="primary">
              Université de Ngozi
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Tableau de bord
            </Typography>
          </Box>
          <form onSubmit={handleSubmit}>
            {resetMode ? (
              <>
                <TextField
                  fullWidth
                  label="E-mail"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Code OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                  placeholder="123456"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Nouveau mot de passe"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  inputProps={{ minLength: 6 }}
                  sx={{ mb: 2 }}
                />
                <Button fullWidth variant="contained" type="submit" disabled={loading} sx={{ py: 1.5 }}>
                  {loading ? <CircularProgress size={24} /> : 'Réinitialiser le mot de passe'}
                </Button>
                <Button fullWidth onClick={() => setResetMode(false)} sx={{ mt: 1 }}>
                  Retour
                </Button>
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="E-mail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  sx={{ mb: 2 }}
                />
                {!forgotMode && (
                  <TextField
                    fullWidth
                    label="Mot de passe"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />
                )}
                {!forgotMode && (
                  <Box sx={{ textAlign: 'right', mb: 2 }}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => setForgotMode(true)}
                    >
                      Mot de passe oublié ?
                    </Link>
                  </Box>
                )}
                <Button fullWidth variant="contained" type="submit" disabled={loading} sx={{ py: 1.5 }}>
                  {loading ? <CircularProgress size={24} /> : forgotMode ? 'Envoyer le code' : 'Se connecter'}
                </Button>
                {forgotMode && (
                  <Button fullWidth onClick={() => setForgotMode(false)} sx={{ mt: 1 }}>
                    Retour
                  </Button>
                )}
              </>
            )}
          </form>
        </Paper>
      </motion.div>
    </Box>
  );
}
